import {useEffect, useState} from "react";
import { Form, Accordion, Button, Alert } from "react-bootstrap";
import { restApiBase } from "../settings";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { InfoCircle } from "react-bootstrap-icons";
import {ScopePopover} from "./ScopePopover";

export function ProjectSovereign({ userId, token }) {
    const [countries, setCountries] = useState([
        { value: "USA", label: "USA" },
        { value: "France", label: "France" },
        { value: "Germany", label: "Germany" },
    ]);

    useEffect(() => {
        console.log("Fetch countries");
        fetch("https://restcountries.com/v3.1/all?fields=name")
            .then(res => res.json())
            .then(data => {
                console.log("load...");
                if (Array.isArray(data)) {
                    const options = data
                        .map(c => ({ value: c.name.common, label: c.name.common }))
                        .sort((a, b) => a.label.localeCompare(b.label));
                    setCountries(options);
                }
            })
            .catch(err => console.error(err));
    }, []);

    const scopes = [
        {
            name: "Economy",
            description:
                "Research 5 pillars of sovereign risk (Economy, Fiscal, Monetary, External, and Government and Politics) and implications for macro structure and policy stance, current economy performance, and forward trajectory of the economy.",
        },
        {
            name: "Fiscal",
            description:
                "Research 5 pillars of sovereign risk (Economy, Fiscal, Monetary, External, and Government and Politics) and implications for fiscal state, fiscal outlook/risks and future trajectory.",
        },
        {
            name: "Monetary",
            description:
                "Research 5 pillars of sovereign risk (Economy, Fiscal, Monetary, External, and Government and Politics) and implications for state of monetary policy, developments and future trajectory.",
        },
        {
            name: "External",
            description:
                "Research 5 pillars of sovereign risk (Economy, Fiscal, Monetary, External, and Government and Politics) and implications for external sector current state, developments and future trajectory.",
        },
        {
            name: "Government and Politics",
            description:
                "Research 5 pillars of sovereign risk (Economy, Fiscal, Monetary, External, and Government and Politics) and implications for state of the government, governance and politics, future developments.",
        },
    ];

    const categories = {
        Commodities: ["Oil", "Gas", "Coal", "Copper", "Rare Metals", "Gold", "Other"],
        Macro: [
            "External Debt",
            "Current Account",
            "Budget Deficit/Surplus",
            "Trade",
            "Foreign Direct Investments",
            "FX and Currency Reserves",
            "Interest Rates",
            "Inflation",
        ],
        Government: [
            "Taxation",
            "Government Reform",
            "Elections and Policies",
            "Judicial Affairs",
            "Safety and Security",
        ],
        Other: ["Geopolitics", "Financial Asset Performance", "Intellectual Property", "Technology and AI"],
    };

    const [country, setCountry] = useState("");
    const [selectedScope, setSelectedScope] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [processing, setProcessing] = useState(false);
    const [warning, setWarning] = useState(null);

    const navigate = useNavigate();

    const toggleScope = (scope) => {
        setSelectedScope(prev => prev.includes(scope)
            ? prev.filter(s => s !== scope)
            : [...prev, scope]);
    };

    const toggleCategory = (category) => {
        setSelectedCategories(prev => prev.includes(category)
            ? prev.filter(c => c !== category)
            : [...prev, category]);
    };

    const allowRun = country && selectedScope.length > 0;

    const runReport = () => {
        if (!allowRun) return;

        setProcessing(true);
        setWarning(null);

        const today = new Date();
        const timeframe = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(today);

        const requestData = {
            report_type: "SAR_COUNTRY",
            parameters: {
                target: country?.value || country,
                scope: selectedScope,
                categories: selectedCategories,
                timeframe: timeframe,
            },
            options: {
                risk_factors: selectedCategories,
            },
        };

        console.log("Starting SAR_COUNTRY report for %s from %s", country, restApiBase)


        fetch(`${restApiBase}run_report?user_id=${userId}`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json;charset=utf-8"
            },
            body: JSON.stringify(requestData)
        })
            .then(res => res.json())
            .then(result => {
                setProcessing(false);
                if (result?.report_id) {
                    navigate(`/report/run/${result.report_id}`);
                } else if (result?.error) {
                    setWarning(`${result.error}: ${result.error_description}`);
                }
            })
            .catch(err => {
                setProcessing(false);
                setWarning(`Network error: ${err.message}`);
            });
    };
    const options = countries.map(c => ({ value: c, label: c }));

    return (
        <div className="row">
            <div className="col">
                <h3 className="mb-3">Sovereign Analysis</h3>

                <Form.Group className="mb-3">
                    <Form.Label>Select Country</Form.Label>
                    <Select
                        options={countries}  // массив объектов { value, label }
                        value={country}      // выбранный объект { value, label }
                        onChange={setCountry} // получает объект { value, label }
                        isClearable
                    />
                </Form.Group>


                <Form.Group className="mb-3">
                    <Form.Label>Scope of Analysis</Form.Label>
                    <div>
                        {scopes.map(({ name, description }) => (
                            <div
                                key={name}
                                className="d-inline-flex align-items-center me-3 mb-2"
                            >
                                <div>
                                <Form.Check

                                    label={name}
                                    type="checkbox"
                                    checked={selectedScope.includes(name)}
                                    onChange={() => toggleScope(name)}
                                />
                                </div>

                                <ScopePopover name={name} description={description} />
                            </div>
                        ))}
                    </div>
                </Form.Group>


                <Accordion className="mb-3">
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>Risk Factors / Categories</Accordion.Header>
                        <Accordion.Body>
                            {Object.entries(categories).map(([groupName, items]) => (
                                <div key={groupName} className="mb-2">
                                    <strong>{groupName}</strong>
                                    <div className="d-flex flex-wrap gap-2 mt-1">
                                        {items.map(cat => (
                                            <Form.Check
                                                key={cat}
                                                inline
                                                label={cat}
                                                type="checkbox"
                                                checked={selectedCategories.includes(cat)}
                                                onChange={() => toggleCategory(cat)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>

                {warning && (
                    <Alert variant="danger" dismissible onClose={() => setWarning(null)}>
                        {warning}
                    </Alert>
                )}

                <div className="my-3">
                    <Button disabled={!allowRun || processing} onClick={runReport}>
                        {processing ? "Processing..." : "Run report"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
